import React, { useState, useEffect } from 'react';
import { useAuth } from '../store/auth';
import { useGroups } from '../store/groups';
import { Field, Input, Textarea, PrimaryBtn, SecondaryBtn, ErrorMsg, BottomSheet } from '../components/UI';
import { isValidGroupName, isValidInviteCode, sanitizeText } from '../lib/validation';

type Step = 'welcome' | 'create' | 'join';

export const WelcomePage: React.FC = () => {
  const { user, profile, signOut, deleteAccount } = useAuth();
  const { createGroup, joinByCode } = useGroups();
  const [step, setStep] = useState<Step>('welcome');
  const [groupName, setGroupName] = useState('');
  const [groupDesc, setGroupDesc] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const firstName = profile?.full_name?.split(' ')[0] || 'der';

  useEffect(() => {
    const code = sessionStorage.getItem('pendingInvite');
    if (code) {
      setInviteCode(code);
      setStep('join');
    }
  }, []);

  const handleCreate = async () => {
    const nameCheck = isValidGroupName(groupName);
    if (!nameCheck.ok) { setError(nameCheck.message); return; }
    setLoading(true); setError('');
    const err = await createGroup(sanitizeText(groupName, 100), sanitizeText(groupDesc, 300), user!.id);
    if (err) setError(err);
    setLoading(false);
  };

  const handleJoin = async () => {
  if (!inviteCode.trim()) { setError('Skriv inn invitasjonskoden'); return; }
  if (!isValidInviteCode(inviteCode)) { setError('Koden må være 6 tegn'); return; }
  setLoading(true); setError('');
  sessionStorage.removeItem('pendingInvite');
  sessionStorage.removeItem('pendingInviteEmail');
  const err = await joinByCode(inviteCode.trim(), user!.id);
  if (err) { setError(err); setLoading(false); return; }
  setLoading(false);
};

  const handleDeleteAccount = async () => {
    setDeleting(true); setDeleteError('');
    const err = await deleteAccount();
    if (err) { setDeleteError(err); setDeleting(false); }
  };

  if (step === 'create') return (
    <div style={{ padding: '32px 20px', display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
      <button onClick={() => { setStep('welcome'); setError(''); }} style={{ background: 'none', border: 'none', fontSize: 24, marginBottom: 20, alignSelf: 'flex-start' }}>←</button>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Opprett gruppe</h2>
      <p style={{ color: 'var(--text2)', marginBottom: 24, fontSize: 14 }}>Gi gruppen et navn – f.eks. «Vennegjengen» eller «Solvang Borettslag».</p>
      {error && <ErrorMsg msg={error} />}
      <Field label="Gruppenavn"><Input placeholder="F.eks. Vennegjengen" value={groupName} onChange={e => setGroupName(e.target.value)} /></Field>
      <Field label="Beskrivelse (valgfritt)"><Textarea placeholder="Hvem er med, hva handler gruppen om..." value={groupDesc} onChange={e => setGroupDesc(e.target.value)} rows={3} /></Field>
      <PrimaryBtn onClick={handleCreate} disabled={loading}>{loading ? 'Oppretter...' : 'Opprett gruppe'}</PrimaryBtn>
    </div>
  );

  if (step === 'join') return (
    <div style={{ padding: '32px 20px', display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
      <button onClick={() => { setStep('welcome'); setError(''); }} style={{ background: 'none', border: 'none', fontSize: 24, marginBottom: 20, alignSelf: 'flex-start' }}>←</button>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Bli med i gruppe</h2>
      <p style={{ color: 'var(--text2)', marginBottom: 24, fontSize: 14 }}>Skriv inn invitasjonskoden du har fått fra en gruppeadmin.</p>
      {error && <ErrorMsg msg={error} />}
      <Field label="Invitasjonskode">
        <Input placeholder="F.eks. ABC123" value={inviteCode} onChange={e => setInviteCode(e.target.value.toUpperCase())} style={{ textTransform: 'uppercase', letterSpacing: 4, fontSize: 20, textAlign: 'center' }} />
      </Field>
      <PrimaryBtn onClick={handleJoin} disabled={loading}>{loading ? 'Kobler til...' : 'Bli med'}</PrimaryBtn>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Minimal topbar med innstillinger */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 16px 0' }}>
        <button
          onClick={() => setShowSettings(true)}
          style={{ background: 'none', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', padding: '7px 13px', fontSize: 13, color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          ⚙️ Innstillinger
        </button>
      </div>

      {/* Innhold */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '20px 24px 40px', textAlign: 'center' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>👋</div>
        <h1 style={{ fontSize: 26, fontWeight: 700, marginBottom: 8, letterSpacing: -0.4 }}>Hei, {firstName}!</h1>
        <p style={{ fontSize: 15, color: 'var(--text2)', marginBottom: 40, maxWidth: 280, lineHeight: 1.6 }}>
          Velkommen til Toolshare.no! For å komme i gang trenger du en gruppe – enten lager du en ny, eller bli med i en eksisterende.
        </p>
        <div style={{ width: '100%', maxWidth: 320 }}>
          <PrimaryBtn onClick={() => setStep('create')}>🏗 Opprett ny gruppe</PrimaryBtn>
          <SecondaryBtn onClick={() => setStep('join')}>🔑 Bli med med invitasjonskode</SecondaryBtn>
        </div>
      </div>

      {/* Innstillinger sheet */}
      {showSettings && (
        <BottomSheet title="Innstillinger" onClose={() => setShowSettings(false)}>
          {/* Profil info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, color: 'var(--green-dark)' }}>
              {profile?.full_name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
            </div>
            <div>
              <div style={{ fontWeight: 600 }}>{profile?.full_name}</div>
              <div style={{ fontSize: 13, color: 'var(--text2)' }}>{profile?.email}</div>
            </div>
          </div>

          <button
            onClick={signOut}
            style={{ width: '100%', padding: 13, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', fontSize: 15, fontWeight: 500, color: 'var(--text)', marginBottom: 10, cursor: 'pointer' }}
          >
            🚪 Logg ut
          </button>

          <button
            onClick={() => { setShowSettings(false); setTimeout(() => setShowDeleteConfirm(true), 300); }}
            style={{ width: '100%', padding: 13, borderRadius: 'var(--radius-sm)', border: '1px solid var(--red-light)', background: 'none', fontSize: 15, fontWeight: 500, color: 'var(--red)', cursor: 'pointer' }}
          >
            🗑 Slett konto
          </button>

          <SecondaryBtn onClick={() => setShowSettings(false)}>Lukk</SecondaryBtn>
        </BottomSheet>
      )}

      {/* Slett konto bekreftelse */}
      {showDeleteConfirm && (
        <BottomSheet title="Slett konto" onClose={() => setShowDeleteConfirm(false)}>
          {deleteError && <ErrorMsg msg={deleteError} />}
          <div style={{ background: 'var(--red-light)', borderRadius: 'var(--radius-sm)', padding: 14, marginBottom: 18 }}>
            <div style={{ fontWeight: 600, color: 'var(--red)', marginBottom: 6 }}>⚠️ Dette kan ikke angres!</div>
            <div style={{ fontSize: 14, color: 'var(--red)', lineHeight: 1.6 }}>
              Alle dine verktøy, lånhistorikk, kommentarer og gruppemedlemskap slettes permanent.
            </div>
          </div>
          <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 20 }}>
            Er du helt sikker på at du vil slette kontoen din?
          </p>
          <button
            onClick={handleDeleteAccount}
            disabled={deleting}
            style={{ width: '100%', padding: 13, background: 'var(--red)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: deleting ? 0.7 : 1 }}
          >
            {deleting ? 'Sletter...' : '🗑 Ja, slett kontoen min'}
          </button>
          <SecondaryBtn onClick={() => setShowDeleteConfirm(false)}>Avbryt</SecondaryBtn>
        </BottomSheet>
      )}
    </div>
  );
};
